import { useRef, useState } from "react";
import { Play } from "lucide-react";

type VideoCard = {
  key: string;
  label: string;
  caption?: string;
  src: string;
  poster: string;
};

const cards: VideoCard[] = [
  {
    key: "what",
    label: "What is Quizrun?",
    src: "/videos/what.mp4",
    poster: "/images/videos/what-poster.webp",
  },
  {
    key: "trail",
    label: "Quizrun Trail · Box Hill",
    caption: "A hiking edition in the Surrey Hills.",
    src: "/videos/trail.mp4",
    poster: "/images/videos/trail-poster.webp",
  },
];

export const InActionSection = () => {
  const [playing, setPlaying] = useState<string | null>(null);
  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({});

  const play = (key: string) => {
    // Only one at a time — pause the other before starting this one.
    Object.entries(videoRefs.current).forEach(([k, v]) => {
      if (k !== key && v) v.pause();
    });
    setPlaying(key);
    const v = videoRefs.current[key];
    if (v) v.play();
  };

  return (
    <section className="py-16 md:py-24 relative">
      <div className="container mx-auto px-5 md:px-8">
        {/* Section header */}
        <div className="max-w-2xl">
          <div className="text-xs font-bold uppercase tracking-widest text-brand-orange mb-2">
            Watch
          </div>
          <h2 className="text-2xl md:text-4xl font-black tracking-tight text-foreground">
            See Quizrun in action
          </h2>
        </div>

        {/* Two portrait cards — side by side on desktop, stacked on mobile */}
        <div className="mt-10 flex flex-col md:flex-row items-center md:items-start justify-center gap-8 md:gap-10">
          {cards.map((card, i) => {
            const isPlaying = playing === card.key;
            return (
              <div
                key={card.key}
                className="w-full max-w-[340px] flex flex-col items-center"
              >
                <div className="text-sm font-bold text-foreground text-center mb-3">
                  {card.label}
                  {card.caption && (
                    <span className="block text-xs font-medium text-muted-foreground mt-0.5">
                      {card.caption}
                    </span>
                  )}
                </div>

                <div
                  className={`relative w-full aspect-[9/16] rounded-xl overflow-hidden bg-muted shadow-lg border ${
                    i === 0 ? "border-brand-orange/30 ring-1 ring-brand-orange/20" : "border-border"
                  }`}
                >
                  <video
                    ref={(el) => { videoRefs.current[card.key] = el; }}
                    src={card.src}
                    poster={card.poster}
                    preload="none"
                    playsInline
                    controls={isPlaying}
                    title={card.label}
                    aria-label={card.label}
                    onPause={() => setPlaying((p) => (p === card.key ? null : p))}
                    onEnded={() => setPlaying((p) => (p === card.key ? null : p))}
                    className="w-full h-full object-cover"
                  />

                  {!isPlaying && (
                    <button
                      type="button"
                      onClick={() => play(card.key)}
                      aria-label={`Play video: ${card.label}`}
                      className="absolute inset-0 flex items-center justify-center bg-black/10 hover:bg-black/20 transition-colors group"
                    >
                      <span className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        <Play className="h-7 w-7 text-brand-orange fill-brand-orange translate-x-0.5" />
                      </span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
